// Packaging status enum
export enum PackagingStatus {
  Active = 1,
  Inactive = 2,
}

// Date type enum for search
export enum PackagingDateTypes {
  Created = 1,
  Status = 2,
}

// Packaging weight type enum
export enum PackagingWeightType {
  Center = 10, // Pesada en Centro
  Plant = 20, // Pesada en Planta
}

export class PackagingHelper {
  /**
   * Get status color class based on status
   */
  static GetStatusColor(status: number): string {
    switch (status) {
      case PackagingStatus.Active:
        return 'bg-success';
      case PackagingStatus.Inactive:
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Format weight display with Spanish number format (20.000,342)
   */
  static FormatWeight(weight: number | undefined, measure: string): string {
    if (!weight || weight === 0) {
      return `0 ${measure}`;
    }
    return `${this.formatNumber(weight, 2)} ${measure}`;
  }

  /**
   * Format number with Spanish format (thousands separator: dot, decimal separator: comma)
   */
  private static formatNumber(value: number, decimals: number): string {
    const parts = value.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return parts.join(',');
  }

  /**
   * Get the last weight by type from packaging weights array
   * @param weights Array of packaging weights
   * @param type Weight type (10=Center, 20=Plant)
   * @returns Last weight of specified type or null
   */
  static GetLastWeightByType(
    weights: any[] | undefined,
    type: PackagingWeightType
  ): any | null {
    if (!weights || weights.length === 0) {
      return null;
    }

    // Filter by type and sort by date descending
    const filteredWeights = weights
      .filter((w) => w.type === type)
      .sort(
        (a, b) =>
          new Date(b.weightDate).getTime() - new Date(a.weightDate).getTime()
      );

    return filteredWeights.length > 0 ? filteredWeights[0] : null;
  }

  /**
   * Get formatted last center weight (Type 10)
   */
  static GetLastCenterWeight(weights: any[] | undefined): string {
    const lastWeight = this.GetLastWeightByType(
      weights,
      PackagingWeightType.Center
    );
    return lastWeight ? `${this.formatNumber(lastWeight.weight, 3)} Kg` : '';
  }

  /**
   * Get formatted last center net weight (Type 10)
   */
  static GetLastCenterNetWeight(weights: any[] | undefined, tare: number | undefined): string {
    const lastWeight = this.GetLastWeightByType(
      weights,
      PackagingWeightType.Center
    );
    const weight = lastWeight && lastWeight.weight - (tare || 0) >= 0 ? lastWeight.weight - (tare || 0) : 0;
    return lastWeight ? `${this.formatNumber(weight, 3)} Kg` : '';
  }

  /**
   * Get formatted last plant weight (Type 20)
   */
  static GetLastPlantWeight(weights: any[] | undefined): string {
    const lastWeight = this.GetLastWeightByType(
      weights,
      PackagingWeightType.Plant
    );
    return lastWeight ? `${this.formatNumber(lastWeight.weight, 3)} Kg` : '';
  }

  /**
   * Get formatted last plant net weight (Type 20)
   */
  static GetLastPlantNetWeight(weights: any[] | undefined, tare: number | undefined): string {
    const lastWeight = this.GetLastWeightByType(
      weights,
      PackagingWeightType.Plant
    );
    const weight = lastWeight && lastWeight.weight - (tare || 0) >= 0 ? lastWeight.weight - (tare || 0) : 0;
    return lastWeight ? `${this.formatNumber(weight, 3)} Kg` : '';
  }

  /**
   * Format tare weight field
   */
  static FormatTareWeight(tare: number | undefined, measure: string): string {
    if (!tare || tare === 0) {
      return `0 ${measure}`;
    }
    return `${this.formatNumber(tare, 3)} ${measure}`;
  }
}
